/* eslint-disable react-hooks/exhaustive-deps */
import { useApolloClient } from "@apollo/client";
import Button from "@mostrans/web-components/components/base/Button";
import Dropzone from "@mostrans/web-components/components/base/Dropzone";
import { useConfirmationModal } from "@mostrans/web-components/hooks/useConfirmationModal";
import {
  Avatar,
  Box,
  Chip,
  Container,
  Divider,
  Grid,
  Typography,
  useMediaQuery
} from "@mui/material";
import { isEmpty } from "lodash";
import React, { useEffect, useState } from "react";
import { useSetState } from "react-use";
import { useSetRecoilState } from "recoil";
import {
  deleteCandidateCourseByID,
  deleteCandidateEducationByID,
  deleteCandidateExperienceByID,
  updateCVCandidate,
} from "../../../graphql/mutations";
import { getCandidateByID } from "../../../graphql/query";
import { BLOB_TYPE } from "../../../shared/constants";
import { uploadCVCandidate } from "../../../shared/firebaseServices";
import { globalLoadingAtom } from "../../../shared/globalStates";
import { generateFileName, toLongDate, toastError, toastSuccess } from "../../../shared/helpers";
import AdditionalInfo from "./Profile/AdditionalInfo/AdditionalInfo";
import Course from "./Profile/Course";
import Education from "./Profile/Education";
import Experience from "./Profile/Experiences";
import ProfileEdit from "./Profile/Profile";
import Skills from "./Profile/Skills";

function Profile() {
  const media = useMediaQuery("(max-width:900px)");
  const client = useApolloClient();
  const [data, setData] = useSetState([]);
  const [pdfFile, setPdfFile] = useState({});
  const [invalidMessage, setInvalidMessage] = useState("Only .pdf files are accepted!!");
  const [openExperiences, setOpenExperiences] = useState([false, false]);
  const [openEducation, setOpenEducation] = useState([false, false]);
  const [openCourse, setOpenCourse] = useState([false, false]);
  const [openProfile, setOpenProfile] = useState(false);
  const [openSkills, setOpenSkills] = useState(false);
  const [index, setIndex] = useState(null);
  const { showConfirmationModal } = useConfirmationModal();
  const setLoading = useSetRecoilState(globalLoadingAtom);
  useEffect(() => {
    getCandidateByID(client).then(setData);
  }, []);

  const handleClose = () => {
    setOpenExperiences([false, false]);
    setOpenEducation([false, false]);
    setOpenProfile(false);
    setOpenCourse([false, false]);
    setOpenSkills(false);
    setIndex(null);
  };

  const confirmationAlert = async () => {
    const show = await showConfirmationModal({
      contentText: "Are you sure ?",
    });
    return show;
  };

  const deleteExperience = async (id, index) => {
    const response = await confirmationAlert();
    if (response.isConfirmed) {
      const result = await deleteCandidateExperienceByID(client, id);
      if (result.status !== "000") {
        toastError(result.errMsg);
      } else {
        toastSuccess("Delete experience success");
        var array = [...data.Experiences];
        array.splice(index, 1);
        setData({ Experiences: array });
      }
    }
  };

  const deleteEducation = async (id, index) => {
    const response = await confirmationAlert();
    if (response.isConfirmed) {
      const result = await deleteCandidateEducationByID(client, id);
      if (result.status !== "000") {
        toastError(result.errMsg);
      } else {
        toastSuccess("Delete education success");
        var array = [...data.Educations];
        array.splice(index, 1);
        setData({ Educations: array });
      }
    }
  };

  const deleteCourse = async (id, index) => {
    const response = await confirmationAlert();
    if (response.isConfirmed) {
      const result = await deleteCandidateCourseByID(client, id);
      if (result.status !== "000") {
        toastError(result.errMsg);
      } else {
        toastSuccess("Delete course success");
        var array = [...data.Courses];
        array.splice(index, 1);
        setData({ Courses: array });
      }
    }
  };

  const handlePdf = (files) => {
    const isPdf = () => {
      return files.type === BLOB_TYPE.PDF;
    };
    if (!isPdf) return;
    setPdfFile(files);
  };

  const handleUpload = async () => {
    setLoading(true);
    const cv_name = generateFileName("resume-", pdfFile.name);
    const link_cv = await uploadCVCandidate(pdfFile, cv_name);
    setLoading(false);
    if (!isEmpty(link_cv)) {
      const updateCV = await updateCVCandidate(client, link_cv);
      if (updateCV.status !== "000") {
        toastError();
      } else {
        toastSuccess("Update CV Success");
        setPdfFile({});
        setData({ ...data, link_cv: link_cv });
      }
    }
  };

  return (
    <React.Fragment>
      <Container
        maxWidth="lg"
        disableGutters={media}
        sx={{
          border: "1px solid rgba(0, 0, 0, 0.12)",
          borderRadius: "4px",
          padding: "10px",
        }}>
        <Typography
          textAlign="center"
          pt={2}
          pb={media && 2}
          textTransform="uppercase"
          variant="h2"
          component="h2">
          Profile Information
        </Typography>
        <Box
          sx={{
            bgcolor: "white",
          }}>
          <Grid container spacing={1} pb="20px">
            <Grid item lg={3} md={3} sm={12} sx={{ margin: "auto" }}>
              <Avatar
                alt={data?.full_name}
                src={data?.link_photo}
                sx={{
                  margin: "auto",
                  height: "100px",
                  width: "100px",
                }}
              />
            </Grid>
            <Grid item lg={9} md={9} sm={12}>
              <Box component="div" sx={{ marginY: "20px" }}>
                <Box component="div" width="100%">
                  <Box
                    my={2}
                    textAlign="center"
                    sx={{
                      display: media ? "" : "inline-flex",
                      width: "100%",
                      justifyContent: media && "center",
                    }}>
                    <Typography
                      pr={media ? 0 : 2}
                      sx={{
                        fontWeight: "500",
                        fontSize: "20px",
                      }}>
                      {data?.full_name}
                    </Typography>
                    <ProfileEdit
                      open={openProfile}
                      setOpen={setOpenProfile}
                      handleClose={handleClose}
                      data={data}
                      setData={setData}
                    />
                  </Box>
                  <Grid container spacing={1}>
                    <Grid item my={0.3} xl={3} lg={3} md={3} sm={6} xs={6}>
                      <Typography
                        className="text-textSecondary"
                        sx={{
                          fontSize: "14px",
                          fontWeight: "500",
                          marginY: "5px",
                        }}>
                        Address
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "14px",
                          fontWeight: "400",
                          wordWrap: "break-word",
                        }}>
                        {data?.address?.split(",").at(0)}
                      </Typography>
                    </Grid>
                    <Grid item my={0.3} xl={9} lg={9} md={9} sm={6} xs={6}>
                      <Typography
                        className="text-textSecondary"
                        sx={{
                          fontSize: "14px",
                          fontWeight: "500",
                          marginY: "5px",
                        }}>
                        Gender
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "14px",
                          fontWeight: "400",
                          wordWrap: "break-word",
                        }}>
                        {data?.gender === "M" ? "MALE" : "FEMALE"}
                      </Typography>
                    </Grid>
                    <Grid item my={0.3} xl={3} lg={3} md={3} sm={6} xs={6}>
                      <Typography
                        className="text-textSecondary"
                        sx={{
                          fontSize: "14px",
                          fontWeight: "500",
                          marginY: "5px",
                        }}>
                        Phone Number
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "14px",
                          fontWeight: "400",
                          wordWrap: "break-word",
                        }}>
                        {data?.phone_number}
                      </Typography>
                    </Grid>
                    <Grid item my={0.3} xl={9} lg={9} md={9} sm={6} xs={6}>
                      <Typography
                        className="text-textSecondary"
                        sx={{
                          fontSize: "14px",
                          fontWeight: "500",
                          marginY: "5px",
                        }}>
                        Email
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "14px",
                          fontWeight: "400",
                          wordWrap: "break-word",
                        }}>
                        {data?.email}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Box>
        <Divider light />
        <Box sx={{ bgcolor: "white" }}>
          <Box pb={5}>
            <Box>
              <Grid container spacing={1} py={2}>
                <Grid item lg={6} md={6} sm={12} xs={12}>
                  <Typography variant="h4" component="h4">
                    Work Experience
                  </Typography>
                </Grid>
                <Grid item lg={6} md={6} sm={12} xs={12}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: media ? "flex-start" : "flex-end",
                    }}>
                    <Experience
                      open={openExperiences}
                      setOpen={setOpenExperiences}
                      handleClose={handleClose}
                      data={data}
                      setData={setData}
                      index={index}
                    />
                  </Box>
                </Grid>
              </Grid>
            </Box>
            <Box>
              <Box>
                <Grid
                  container
                  spacing={1}
                  justifyContent={!isEmpty(data?.Experiences) ? "space-between" : "center"}>
                  {!isEmpty(data?.Experiences) ? (
                    data?.Experiences.map((result, index) => (
                      <React.Fragment key={index}>
                        <Grid
                          item
                          xl={2}
                          md={2}
                          sm={2}
                          xs={2}
                          sx={{
                            lineHeight: "24px",
                            display: media && "none",
                          }}>
                          <Typography
                            paddingY="5px"
                            sx={{
                              fontWeight: "500",
                              fontSize: "14px",
                            }}>
                            {`${toLongDate(new Date(result.start_date))} - ${result.end_date ? toLongDate(new Date(result.end_date)) : "Present"
                              }`}
                          </Typography>
                        </Grid>
                        <Grid item xl={8} lg={8} md={8} sm={8} xs={6}>
                          <Typography variant="h5" component="h5" paddingY="5px">
                            {result.job_position}
                          </Typography>
                          <Typography fontSize={14} fontWeight={400}>
                            {result.company}
                          </Typography>
                          {media && (
                            <Typography
                              sx={{
                                fontWeight: "400",
                                display: "flex",
                              }}>
                              {`${toLongDate(new Date(result.start_date))} - ${result.end_date ? toLongDate(new Date(result.end_date)) : "Present"
                                }`}
                            </Typography>
                          )}

                          <Box style={{ wordWrap: "break-word" }}>
                            <Typography fontSize={14} fontWeight={500} paddingTop="10px">
                              Job Description
                            </Typography>
                            <Typography
                              fontSize={14}
                              fontWeight={400}
                              color="#8c8c8c"
                              whiteSpace="pre-line">
                              {result.detail}
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xl={2} lg={2} md={2} textAlign="end" py={media && 1}>
                          <Button
                            variant="text"
                            color="primary"
                            size="small"
                            type="tertiary"
                            onClick={() => {
                              setOpenExperiences([true, true]);
                              setIndex(index);
                            }}
                            startIcon={<span className="material-icons"> edit </span>}
                            sx={{
                              fontSize: "14px",
                            }}>
                            Edit
                          </Button>
                          <Button
                            variant="text"
                            color="danger"
                            size="small"
                            type="tertiary"
                            startIcon={<span className="material-icons"> delete </span>}
                            onClick={() => deleteExperience(result.id, index)}
                            sx={{
                              fontSize: "14px",
                            }}>
                            Delete
                          </Button>
                        </Grid>
                      </React.Fragment>
                    ))
                  ) : (
                    <Grid item>
                      <p
                        className="font-size-small text-textSecondary"
                        style={{ textAlign: "center" }}>
                        Add your work experience to make it easier for HRD to review prospective
                        workers.
                      </p>
                    </Grid>
                  )}
                </Grid>
              </Box>
            </Box>
          </Box>
          <Divider light={true} />
          <Box pb={5}>
            <Box>
              <Grid container spacing={1} py={2}>
                <Grid item lg={6} md={6} sm={12} xs={12}>
                  <Typography variant="h4" component="h4">
                    Education
                  </Typography>
                </Grid>
                <Grid item lg={6} md={6} sm={12} xs={12}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: media ? "flex-start" : "flex-end",
                    }}>
                    <Education
                      open={openEducation}
                      setOpen={setOpenEducation}
                      handleClose={handleClose}
                      data={data}
                      setData={setData}
                      index={index}
                    />
                  </Box>
                </Grid>
              </Grid>
            </Box>
            <Box>
              <Box>
                <Grid
                  container
                  spacing={1}
                  justifyContent={!isEmpty(data?.Educations) ? "space-between" : "center"}>
                  {!isEmpty(data?.Educations) ? (
                    data?.Educations.map((result, index) => (
                      <React.Fragment key={index}>
                        <Grid
                          item
                          xl={2}
                          md={2}
                          sm={2}
                          xs={2}
                          sx={{
                            lineHeight: "24px",
                            display: media && "none",
                          }}>
                          <Typography
                            paddingY="5px"
                            sx={{
                              fontWeight: "500",
                              fontSize: "14px",
                            }}>
                            {`${result.start_year} - ${result.graduate_year ? result.graduate_year : "Present"
                              } `}
                          </Typography>
                        </Grid>
                        <Grid item xl={8} lg={8} md={8} sm={8} xs={6}>
                          <Typography variant="h5" component="h5" paddingY="5px">
                            {result.school_name}
                          </Typography>
                          <Typography fontSize={14} fontWeight={400} paddingY="5px">
                            {`${result.degree} - ${result.major}`}
                          </Typography>
                          {media && (
                            <Typography
                              sx={{
                                fontWeight: "400",
                                display: "flex",
                              }}>
                              {`${result.start_year} - ${result.graduate_year ? result.graduate_year : "Present"
                                } `}
                            </Typography>
                          )}
                          <Typography fontSize={14} fontWeight={400} paddingY="5px">
                            {`Grade - ${result.ipk}`}
                          </Typography>
                        </Grid>
                        <Grid item xl={2} lg={2} md={2} textAlign="end" py={media && 1}>
                          <Button
                            variant="text"
                            color="primary"
                            size="small"
                            type="tertiary"
                            onClick={() => {
                              setOpenEducation([true, true]);
                              setIndex(index);
                            }}
                            startIcon={<span className="material-icons"> edit </span>}
                            sx={{
                              fontSize: "14px",
                            }}>
                            Edit
                          </Button>
                          <Button
                            variant="text"
                            color="danger"
                            size="small"
                            type="tertiary"
                            startIcon={<span className="material-icons"> delete </span>}
                            onClick={() => deleteEducation(result.id, index)}
                            sx={{
                              fontSize: "14px",
                            }}>
                            Delete
                          </Button>
                        </Grid>
                      </React.Fragment>
                    ))
                  ) : (
                    <Grid item>
                      <p
                        className="font-size-small text-textSecondary"
                        style={{ textAlign: "center" }}>
                        Add your educational history to make it easier for HRD to review prospective
                        workers.
                      </p>
                    </Grid>
                  )}
                </Grid>
              </Box>
            </Box>
          </Box>
          <Divider light={true} />
          <Box pb={5}>
            <Box>
              <Grid container spacing={1} py={2}>
                <Grid item lg={6} md={6} sm={12} xs={12}>
                  <Typography variant="h4" component="h4">
                    Course
                  </Typography>
                </Grid>
                <Grid item lg={6} md={6} sm={12} xs={12}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: media ? "flex-start" : "flex-end",
                    }}>
                    <Course
                      open={openCourse}
                      setOpen={setOpenCourse}
                      handleClose={handleClose}
                      data={data}
                      setData={setData}
                      index={index}
                    />
                  </Box>
                </Grid>
              </Grid>
            </Box>
            <Box>
              <Box>
                <Grid
                  container
                  spacing={1}
                  justifyContent={!isEmpty(data?.Courses) ? "space-between" : "center"}>
                  {!isEmpty(data?.Courses) ? (
                    data?.Courses?.map((result, index) => (
                      <React.Fragment key={index}>
                        <Grid
                          item
                          xl={2}
                          md={2}
                          sm={2}
                          xs={2}
                          sx={{
                            lineHeight: "24px",
                            display: media && "none",
                          }}>
                          <Typography
                            paddingY="5px"
                            sx={{
                              fontWeight: "500",
                              fontSize: "14px",
                            }}>
                            {result.course_year}
                          </Typography>
                        </Grid>
                        <Grid item xl={8} lg={8} md={8} sm={8} xs={6}>
                          <Typography variant="h5" component="h5" paddingY="5px">
                            {result.course_organizer}
                          </Typography>
                          <Typography fontSize={14} fontWeight={400}>
                            {result.course_category}
                          </Typography>
                          {media && (
                            <Typography
                              sx={{
                                fontWeight: "400",
                                display: "flex",
                              }}>
                              {result.course_year}
                            </Typography>
                          )}
                        </Grid>
                        <Grid item xl={2} lg={2} md={2} textAlign="end" py={media && 1}>
                          <Button
                            variant="text"
                            color="primary"
                            size="small"
                            type="tertiary"
                            onClick={() => {
                              setOpenCourse([true, true]);
                              setIndex(index);
                            }}
                            startIcon={<span className="material-icons"> edit </span>}
                            sx={{
                              fontSize: "14px",
                            }}>
                            Edit
                          </Button>
                          <Button
                            variant="text"
                            color="danger"
                            size="small"
                            type="tertiary"
                            startIcon={<span className="material-icons"> delete </span>}
                            onClick={() => deleteCourse(result.id, index)}
                            sx={{
                              fontSize: "14px",
                            }}>
                            Delete
                          </Button>
                        </Grid>
                      </React.Fragment>
                    ))
                  ) : (
                    <Grid item>
                      <p
                        className="font-size-small text-textSecondary"
                        style={{ textAlign: "center" }}>
                        Add your Course history to add to your assessment to increase your chances
                        of getting an interview.
                      </p>
                    </Grid>
                  )}
                </Grid>
              </Box>
            </Box>
          </Box>
          <Divider light={true} />
          <Box pb={5}>
            <Box>
              <Grid container spacing={1} py={2}>
                <Grid item lg={6} md={6} sm={12} xs={12}>
                  <Typography variant="h4" component="h4">
                    Skills
                  </Typography>
                </Grid>
                <Grid item lg={6} md={6} sm={12} xs={12}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: media ? "flex-start" : "flex-end",
                    }}>
                    <Skills
                      open={openSkills}
                      setOpen={setOpenSkills}
                      setData={setData}
                      data={data}
                      handleClose={handleClose}
                    />
                  </Box>
                </Grid>
              </Grid>
            </Box>
            <Box>
              <Box>
                {data?.skills && data?.skills.length > 0 ? (
                  data?.skills?.map((result, index) => (
                    <React.Fragment key={index}>
                      <Chip
                        label={result}
                        size="large"
                        style={{
                          marginRight: "10px",
                          marginBottom: "10px",
                        }}
                      />
                    </React.Fragment>
                  ))
                ) : (
                  <p className="font-size-small text-textSecondary" style={{ textAlign: "center" }}>
                    Add your skills to make it easier for HRD to find suitable candidate.
                  </p>
                )}
              </Box>
            </Box>
          </Box>
          <Divider light={true} />
          <Box pb={5}>
            <AdditionalInfo media={media} data={data} setData={setData} />
          </Box>
          <Divider light={true} />
          <Box pb={5}>
            <Box>
              <Grid container spacing={1} py={2}>
                <Grid item lg={12} md={12} sm={12} xs={12}>
                  <Typography variant="h4" component="h4">
                    Resume
                  </Typography>
                </Grid>
              </Grid>
            </Box>
            <Box>
              <Box>
                <Grid container spacing={1}>
                  <Grid item xl={3} md={3} sm={3} xs={5} sx={{ lineHeight: "24px" }}>
                    <Typography
                      sx={{
                        fontWeight: "500",
                        fontSize: "14px",
                        color: "#8C8C8C",
                      }}
                      paddingY="5px">
                      File name
                    </Typography>
                    <Typography
                      sx={{
                        fontWeight: "500",
                        fontSize: "14px",
                        color: "#8C8C8C",
                      }}
                      paddingY="5px">
                      Updated date
                    </Typography>
                  </Grid>
                  <Grid item xl={9} lg={9} md={9}>
                    <Typography fontSize={14} fontWeight={400} paddingY="5px">
                      <a href={data.link_cv} target="_blank" rel="noreferrer">
                        Resume_{data?.short_name?.replace(" ", "")}.pdf
                      </a>
                    </Typography>
                    <Typography fontSize={14} fontWeight={400} paddingY="5px">
                      {data.updated_date}
                    </Typography>
                  </Grid>
                  <Grid item xl={5} lg={5} md={5} sm={12} xs={12}>
                    <Dropzone
                      id="resume"
                      name="resume"
                      onChange={(e) => handlePdf(e[0])}
                      accept=".pdf"
                      middleText={pdfFile.name ? pdfFile.name : "CLICK/DRAG YOUR FILE HERE"}
                      topText={"Upload Your CV!"}
                      bottomText={invalidMessage}
                      onInvalid={(invalidMessage) => {
                        setInvalidMessage(invalidMessage);
                        alert(invalidMessage);
                      }}
                    />
                    <Button
                      type="button"
                      variant="contained"
                      color="primary"
                      disabled={isEmpty(pdfFile.name)}
                      onClick={handleUpload}
                      style={{ margin: "10px 0" }}>
                      Upload
                    </Button>
                  </Grid>
                </Grid>
              </Box>
            </Box>
          </Box>
        </Box>
      </Container>
    </React.Fragment>
  );
}

export default Profile;
